import { useState, useCallback, useEffect } from 'react';

interface CollapsiblePreferences {
  filters: boolean;
  sessionGroups: Record<string, boolean>;
  allExpanded: boolean;
}

const DEFAULT_PREFERENCES: CollapsiblePreferences = {
  filters: false, // Start collapsed to save space
  sessionGroups: {
    draft: true,
    shared: true,
    accessed: true,
    expired: true,
  },
  allExpanded: true,
};

export const useCollapsiblePreferences = (storageKey: string = 'practitioner-collapsible-prefs') => {
  const [preferences, setPreferences] = useState<CollapsiblePreferences>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) } : DEFAULT_PREFERENCES;
    } catch {
      return DEFAULT_PREFERENCES;
    }
  });

  // Save to localStorage whenever preferences change
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(preferences));
  }, [preferences, storageKey]);

  const updateFiltersCollapsed = useCallback((collapsed: boolean) => {
    setPreferences(prev => ({ ...prev, filters: collapsed }));
  }, []);

  const updateSessionGroupCollapsed = useCallback((groupKey: string, collapsed: boolean) => {
    setPreferences(prev => ({
      ...prev,
      sessionGroups: { ...prev.sessionGroups, [groupKey]: collapsed }
    }));
  }, []);

  const collapseAll = useCallback(() => {
    setPreferences(prev => ({
      ...prev,
      allExpanded: false,
      filters: false,
      sessionGroups: Object.keys(prev.sessionGroups).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>)
    }));
  }, []);

  const expandAll = useCallback(() => {
    setPreferences(prev => ({
      ...prev,
      allExpanded: true,
      filters: true,
      sessionGroups: Object.keys(prev.sessionGroups).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {} as Record<string, boolean>)
    }));
  }, []);

  return {
    preferences,
    updateFiltersCollapsed,
    updateSessionGroupCollapsed,
    collapseAll,
    expandAll,
  };
};